import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, shareReplay, Subject, tap } from 'rxjs';
import { LoginDto } from '../models/login-dto';
import { _isNumberValue } from '@angular/cdk/coercion';
import { Router } from '@angular/router';
import { RegistrationDto } from '../models/registration-dto';

export enum ClaimKey {
    name = 'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/name',
    email = 'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress',
    role = 'http://schemas.microsoft.com/ws/2008/06/identity/claims/role',
    expiration = 'exp',
}

@Injectable({ providedIn: 'platform' })
export class AuthService {
    private _isAuthenticated = new Subject<boolean>();
    public isAuthenticated$ = this._isAuthenticated.asObservable();
    private _decodedJWT = {};

    private setCookie(name: string, value: string, days?: number) {
        let expires = '';
        if (days) {
            const date = new Date();
            date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
            expires = '; expires=' + date.toUTCString();
        }
        document.cookie = name + '=' + (value || '') + expires + '; path=/';
    }

    getCookie(name: string): string | null {
        const nameEQ = name + '=';
        const ca = document.cookie.split(';');
        for (let c of ca) {
            c = c.trim();
            if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length);
        }
        return null;
    }

    private eraseCookie(name: string) {
        document.cookie = name + '=; Max-Age=-99999999; path=/';
    }

    constructor(private router: Router, private http: HttpClient) {}

    login(login: LoginDto) {
        return this.http.post<any>('/api/auth/login', login).pipe(
            tap((x) => {
                this.setCookie('id_token', x.token, 7); // 7 days expiry, adjust as needed

                this._isAuthenticated.next(true);
                this.router.navigate(['/']);
            })
        );
    }

    forgotPassword(email: string) {
        email = email.trim();

        const options = {
            params: new HttpParams().set('email', email),
        };
        return this.http.get<any>('/api/auth/forgotPassword', options);
    }

    register(input: RegistrationDto) {
        return this.http.post<any>('api/auth/register', input);
    }

    resetPassword(input: LoginDto) {
        return this.http.post<any>('/api/auth/resetPassword', input);
    }

    logout(reroute?: any[]) {
        this.eraseCookie('id_token');
        this._isAuthenticated.next(false);
        this._decodedJWT = {};

        if (reroute) {
            this.router.navigate(reroute);
        } else {
            this.router.navigate(['/']);
        }
    }

    public isLoggedIn() {
        const token = this.getCookie('id_token');

        if (!token) {
            this._isAuthenticated.next(false);
            return false;
        }

        this.setClaims(token);
        this._isAuthenticated.next(true);
        return true;
    }

    confirmEmail(userId: string, code: string) {
        userId = userId.trim();
        code = code.trim();

        const options = {
            params: new HttpParams().set('userId', userId).set('code', code),
        };
        return this.http.get<any>('/api/auth/ConfirmEmail', options);
    }

    setClaims(token: string) {
        if (token) {
            this._decodedJWT = JSON.parse(window.atob(token.split('.')[1]));
        }
    }

    getName() {
        return this._decodedJWT[ClaimKey.name];
    }
    getRoles() {
        return this._decodedJWT[ClaimKey.role];
    }
    isAdmin() {
        return this.getRoles()?.includes('Admin');
    }
}
